import { FC, useState, useEffect, useCallback } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import LinearProgress from '@mui/material/LinearProgress'

const TOAST_DURATION = 5000
const TICK_INTERVAL = 50

const Wrapper = styled(Box)`
  min-width: 371px;
  max-width: 440px;
  width: fit-content;
  position: fixed;
  right: ${theme.spacing.xLarge}px;
  background: #483646;
  display: flex;
  align-items: center;
  flex-direction: column;
  z-index: 10010;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  bottom: -30px;
  animation: slidein 0.5s forwards;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  box-shadow: 0px 4px 8px 0px rgba(30, 26, 29, 0.2);
  padding: 0;
  overflow: hidden;

  @keyframes slidein {
    from {
      bottom: -30px;
      opacity: 0;
    }
    to {
      bottom: ${theme.spacing.xLarge}px;
      opacity: 1;
    }
  }

  .toast-inner {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 16px;
    padding: 16px;

    .svg-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .toast-text {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.extraThick};
      color: #e9e0e4;
      line-height: 20px;
      flex: 1;
    }
  }

  html[data-theme='light'] & {
    background: #ededf0;
    box-shadow: 0 4px 8px 0 rgba(30, 26, 29, 0.20);

    .toast-inner .toast-text {
      color: #18181f;
    }

    .svg-wrapper svg circle,
    .svg-wrapper svg path,
    .MuiIconButton-root svg path {
      stroke: #18181f;
    }

    .svg-wrapper svg circle[r='0.75'] {
      fill: #18181f;
    }

    .MuiLinearProgress-bar {
      background: #18181f !important;
    }
  }
`

interface Props {
  text: string
  description: string
  handleClose: () => void
  sx?: Record<string, unknown>
}

const MainLayoutToastNexaAI: FC<Props> = ({ text, handleClose, sx }) => {
  const [progress, setProgress] = useState(100)

  const onComplete = useCallback(() => {
    handleClose()
  }, [handleClose])

  useEffect(() => {
    const startTime = Date.now()

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100)
      setProgress(remaining)

      if (remaining <= 0) {
        clearInterval(timer)
        onComplete()
      }
    }, TICK_INTERVAL)

    return () => {
      clearInterval(timer)
    }
  }, [onComplete])

  return (
    <Wrapper sx={sx}>
      <Box className="toast-inner">
        <Box className="svg-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="#E9E0E4" strokeWidth="1.5" />
            <path d="M12 8V12" stroke="#E9E0E4" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="15.5" r="0.75" fill="#E9E0E4" />
          </svg>
        </Box>

        <Typography className="toast-text">{text}</Typography>

        <IconButton onClick={handleClose} sx={{ flexShrink: 0, padding: 0 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 6L18 18M6 18L18 6"
              stroke="#E9E0E4"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </Box>

      <Box sx={{ width: '100%', '.MuiLinearProgress-bar': { background: '#E9E0E4' } }}>
        <LinearProgress variant="determinate" value={progress} sx={{ height: '2px', background: 'transparent' }} />
      </Box>
    </Wrapper>
  )
}

export default MainLayoutToastNexaAI
